import { Box, Container, Heading, SimpleGrid, Text, VStack, Icon } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Sparkles, Heart, Rocket, LucideIcon } from 'lucide-react';

const MotionBox = motion(Box);

const FeatureCard = ({
  icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6 }}
      p={{ base: 4, md: 6 }}
      borderRadius='xl'
      bg='rgba(11, 61, 145, 0.3)'
      border='1px solid'
      borderColor='brand.accent'
      className='cosmic-card'>
      <Icon as={icon} boxSize={{ base: 6, md: 8 }} color='brand.accent' mb={4} />
      <Text color='brand.light' fontSize={{ base: 'md', md: 'lg' }} fontWeight='bold' mb={2}>
        {title}
      </Text>
      <Text color='gray.300' fontSize={{ base: 'sm', md: 'md' }}>
        {description}
      </Text>
    </MotionBox>
  );
};

const Features = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <Box
      id='features'
      py={{ base: 12, md: 20 }}
      bg='brand.dark'
      bgImage="linear-gradient(to bottom, rgba(2, 8, 20, 0.9), rgba(2, 8, 20, 0.95)), url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80')"
      bgAttachment={{ base: 'scroll', md: 'fixed' }}
      bgSize='cover'
      bgPosition='center'>
      <Container maxW='1200px' px={{ base: 4, md: 6 }}>
        <MotionBox
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}>
          <VStack spacing={{ base: 8, md: 12 }}>
            <VStack spacing={{ base: 4, md: 6 }} maxW='800px'>
              <Heading
                as='h2'
                size={{ base: 'lg', md: 'xl' }}
                textAlign='center'
                color='brand.light'
                letterSpacing='wider'
                px={{ base: 2, md: 0 }}>
                Reimagining Developer Workflows
              </Heading>
              <Text
                color='gray.300'
                fontSize={{ base: 'md', md: 'lg' }}
                textAlign='center'
                px={{ base: 2, md: 0 }}>
                At GallifreyAI, we believe that neurodiversity is our greatest strength. We're
                building a future where different ways of thinking aren't just accepted—they're
                celebrated. Our mission is to create tools that adapt to how you work, not the other
                way around.
              </Text>
            </VStack>

            <SimpleGrid
              columns={{ base: 1, sm: 2, lg: 4 }}
              spacing={{ base: 4, md: 6 }}
              width='full'>
              <FeatureCard
                icon={Brain}
                title='Neurodiversity First'
                description='Building tools that embrace different ways of thinking and working, because unique perspectives drive innovation'
              />
              <FeatureCard
                icon={Sparkles}
                title='Adaptive Workflows'
                description='Creating flexible solutions that adapt to your natural work patterns and preferences'
              />
              <FeatureCard
                icon={Heart}
                title='Inclusive by Design'
                description='Ensuring our tools are accessible and comfortable for developers of all neurotypes'
              />
              <FeatureCard
                icon={Rocket}
                title='Innovation Through Diversity'
                description='Leveraging diverse perspectives to solve complex problems in unique ways'
              />
            </SimpleGrid>

            <Box
              p={{ base: 4, md: 6 }}
              borderRadius='xl'
              bg='rgba(11, 61, 145, 0.3)'
              border='1px solid'
              borderColor='brand.accent'
              maxW='800px'
              className='cosmic-card'>
              <Text color='gray.300' fontSize={{ base: 'sm', md: 'lg' }} textAlign='center'>
                We're creating a world where neurodivergent developers can thrive, innovate, and
                build amazing things without barriers. Join us in shaping a more inclusive future
                for software development.
              </Text>
            </Box>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Features;
